if (!customElements.get("m-video-testimonials")) {
  class MVideoTestimonials extends HTMLElement {
    constructor() {
      super();
      this.slider = null;
      this.activeTrigger = null;
      this._onSectionLoad = this._onSectionLoad.bind(this);
      this._onKeyDown = this._onKeyDown.bind(this);
      this._onPlayClick = this._onPlayClick.bind(this);
      this._onCloseClick = this._onCloseClick.bind(this);
      this._onWarm = this._onWarm.bind(this);
      this._onScrubClick = this._onScrubClick.bind(this);
      this._onScrubPointerDown = this._onScrubPointerDown.bind(this);
      this._onScrubPointerMove = this._onScrubPointerMove.bind(this);
      this._onScrubPointerUp = this._onScrubPointerUp.bind(this);
      this._onFlickDown = this._onFlickDown.bind(this);
      this._onFlickMove = this._onFlickMove.bind(this);
      this._onFlickUp = this._onFlickUp.bind(this);
      this._warmVideos = new Map();
    }

    connectedCallback() {
      this.modal = this.querySelector("[data-vt-modal]");
      this.modalMedia = this.querySelector("[data-vt-modal-media]");
      this.closeButtons = this.querySelectorAll("[data-vt-modal-close]");

      this.initSlider();
      this.bindEvents();

      if (window.Shopify && Shopify.designMode) {
        document.addEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    disconnectedCallback() {
      this.destroySlider();
      this.closeModal();
      if (this._scrubSettleTimer) window.clearTimeout(this._scrubSettleTimer);
      this.unbindEvents();

      if (window.Shopify && Shopify.designMode) {
        document.removeEventListener("shopify:section:load", this._onSectionLoad);
      }
    }

    bindEvents() {
      this.addEventListener("click", this._onPlayClick);
      this.addEventListener("pointerenter", this._onWarm, true);
      this.addEventListener("touchstart", this._onWarm, { capture: true, passive: true });
      this.closeButtons.forEach((btn) => btn.addEventListener("click", this._onCloseClick));
      this.bindModalGestures();
    }

    unbindEvents() {
      this.removeEventListener("click", this._onPlayClick);
      this.removeEventListener("pointerenter", this._onWarm, true);
      this.removeEventListener("touchstart", this._onWarm, { capture: true });
      this.closeButtons.forEach((btn) => btn.removeEventListener("click", this._onCloseClick));
      document.removeEventListener("keydown", this._onKeyDown);
      this.unbindModalGestures();
    }

    /* The whole card is a tap target: resolve any click inside a slide to its play button. */
    triggerFromEvent(event) {
      const direct = event.target.closest("[data-vt-play]");
      if (direct) return direct;
      const slide = event.target.closest(".m-video-testimonials__slide");
      if (!slide || !this.contains(slide)) return null;
      return slide.querySelector("[data-vt-play]");
    }

    /* Start fetching the Shopify-hosted video as soon as the card is hovered or touched. */
    _onWarm(event) {
      const trigger = this.triggerFromEvent(event);
      if (!trigger || trigger.disabled || trigger.dataset.videoType !== "shopify") return;
      const url = trigger.dataset.videoUrl;
      if (!url || this._warmVideos.has(url)) return;
      const video = this.buildMedia("shopify", url, "");
      if (!video) return;
      video.autoplay = false;
      video.preload = "auto";
      video.muted = true;
      video.load();
      this._warmVideos.set(url, video);
    }

    _onSectionLoad(event) {
      const sectionId = this.dataset.sectionId || this.closest("[data-section-id]")?.dataset?.sectionId;
      if (!sectionId || !event?.target?.id?.includes(sectionId)) return;
      this.destroySlider();
      this.initSlider();
    }

    _onPlayClick(event) {
      if (this.modal && !this.modal.hidden && this.modal.contains(event.target)) return;
      const trigger = this.triggerFromEvent(event);
      if (!trigger || !this.contains(trigger) || trigger.disabled) return;

      const type = trigger.dataset.videoType;
      if (!type) return;

      event.preventDefault();
      this.openModal(trigger);
    }

    _onCloseClick(event) {
      event.preventDefault();
      this.closeModal();
    }

    _onKeyDown(event) {
      if (event.key === "Escape") {
        this.closeModal();
        return;
      }
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        if (event.target.closest?.(".m-video-testimonials__modal-timeline")) return;
        event.preventDefault();
        this.stepVideo(event.key === "ArrowRight" ? 1 : -1);
      }
    }

    /* Original slides only. Swiper's loop clones share data-vt-index with the source card. */
    playableTriggers() {
      const usable = (trigger) => !trigger.disabled && trigger.dataset.videoType;
      const originals = [...this.querySelectorAll("[data-vt-play]")].filter((trigger) => {
        return usable(trigger) && !trigger.closest(".swiper-slide-duplicate");
      });
      if (originals.length) return originals;
      return [...this.querySelectorAll("[data-vt-play]")].filter(usable);
    }

    canonicalTrigger(trigger) {
      if (!trigger) return null;
      if (!trigger.closest(".swiper-slide-duplicate")) return trigger;
      const index = trigger.dataset.vtIndex;
      return this.playableTriggers().find((item) => item.dataset.vtIndex === index) || trigger;
    }

    initSlider() {
      if (this.slider) return;

      this.swiperContainer = this.querySelector(".m-video-testimonials__swiper");
      if (!this.swiperContainer) return;

      if (typeof MinimogLibs === "undefined" || !MinimogLibs.Swiper) {
        requestAnimationFrame(() => this.initSlider());
        return;
      }

      const slides = this.swiperContainer.querySelectorAll(".swiper-slide");
      if (slides.length < 1) return;

      const prevEl = this.querySelector(".m-video-testimonials__arrow--prev");
      const nextEl = this.querySelector(".m-video-testimonials__arrow--next");

      /* Mouse: ignore a short pull. A card advances only after a longer drag. Touch flicks stay as they are. */
      const finePointer = window.matchMedia("(pointer: fine)").matches;

      this.slider = new MinimogLibs.Swiper(this.swiperContainer, {
        slidesPerView: "auto",
        spaceBetween: 18,
        centeredSlides: true,
        loop: slides.length > 2,
        speed: 500,
        watchOverflow: true,
        grabCursor: true,
        allowTouchMove: true,
        threshold: finePointer ? 12 : 8,
        touchRatio: finePointer ? 0.85 : 1,
        longSwipesRatio: finePointer ? 0.32 : 0.5,
        longSwipesMs: finePointer ? 1 : 300,
        shortSwipes: !finePointer,
        navigation: {
          prevEl,
          nextEl,
        },
        breakpoints: {
          768: {
            centeredSlides: false,
            spaceBetween: 16,
            /* Only loop/swipe when there are more cards than the 5 fully visible */
            loop: slides.length > 5,
          },
        },
      });
    }

    destroySlider() {
      if (this.slider) {
        this.slider.destroy(true, true);
        this.slider = null;
      }
    }

    openModal(trigger, options = {}) {
      if (!this.modal || !this.modalMedia) return;

      const type = trigger.dataset.videoType;
      const videoUrl = trigger.dataset.videoUrl || "";
      const videoId = trigger.dataset.videoId || "";
      let media = null;
      if (type === "shopify" && this._warmVideos.has(videoUrl)) {
        media = this._warmVideos.get(videoUrl);
        this._warmVideos.delete(videoUrl);
        media.muted = false;
        media.autoplay = true;
        media.currentTime = 0;
      } else {
        media = this.buildMedia(type, videoUrl, videoId);
      }
      if (!media) return;

      const previous = this.modalMedia.querySelector("video");
      if (previous && previous !== media) {
        previous.pause();
        previous.removeAttribute("src");
        previous.load();
      }

      const focusScrub = options.focusClose === false
        && this.modal.contains(document.activeElement)
        && !!document.activeElement.closest("[data-vt-scrub]");

      this.activeTrigger = trigger;
      this.modal.setAttribute("aria-label", trigger.dataset.vtLabel || "Video testimonial");
      this.modalMedia.innerHTML = "";
      this.modalMedia.appendChild(media);
      if (this.modal.parentElement !== document.body) {
        this._modalHome = this.modal.parentElement;
        document.body.appendChild(this.modal);
      }
      this.modal.hidden = false;
      document.body.classList.add("m-video-testimonials--modal-open");
      document.removeEventListener("keydown", this._onKeyDown);
      document.addEventListener("keydown", this._onKeyDown);
      this.ensureScrubber(options.scrollScrubber !== false);

      if (options.focusClose !== false) {
        const closeBtn = this.modal.querySelector(".m-video-testimonials__modal-close");
        if (closeBtn) closeBtn.focus();
      } else if (focusScrub) {
        this.modal.querySelector("[data-vt-scrub].is-active")?.focus({ preventScroll: true });
      }

      if (media.tagName === "VIDEO") {
        this.bindSimpleControls(media);
        const playPromise = media.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      }
    }

    stepVideo(delta) {
      const triggers = this.playableTriggers();
      if (triggers.length < 2) return;
      const current = this.canonicalTrigger(this.activeTrigger);
      let index = triggers.indexOf(current);
      if (index < 0) index = 0;
      const nextIndex = (index + delta + triggers.length) % triggers.length;
      this.openModal(triggers[nextIndex], { focusClose: false });
    }

    closeModal() {
      if (!this.modal || this.modal.hidden) return;

      const video = this.modalMedia?.querySelector("video");
      if (video) {
        video.pause();
        video.removeAttribute("src");
        video.load();
      }

      if (this.modalMedia) this.modalMedia.innerHTML = "";
      this.modal.hidden = true;
      if (this._modalHome && this.modal.parentElement === document.body) {
        this._modalHome.appendChild(this.modal);
      }
      document.body.classList.remove("m-video-testimonials--modal-open");
      document.removeEventListener("keydown", this._onKeyDown);

      if (this.activeTrigger) {
        /* preventScroll: focusing a partly clipped card would otherwise scroll the swiper's overflow box */
        this.activeTrigger.focus({ preventScroll: true });
        this.activeTrigger = null;
      }
    }

    bindSimpleControls(video) {
      const toggleBtn = document.createElement("button");
      toggleBtn.type = "button";
      toggleBtn.className = "m-video-testimonials__modal-toggle";
      toggleBtn.setAttribute("aria-label", "Pause");
      toggleBtn.innerHTML =
        '<span class="m-video-testimonials__modal-toggle-icon m-video-testimonials__modal-toggle-icon--pause" aria-hidden="true"></span>' +
        '<span class="m-video-testimonials__modal-toggle-icon m-video-testimonials__modal-toggle-icon--play" aria-hidden="true"></span>';

      this.modalMedia.appendChild(toggleBtn);

      const timeline = document.createElement("div");
      timeline.className = "m-video-testimonials__modal-timeline";
      const seek = document.createElement("input");
      seek.type = "range";
      seek.className = "m-video-testimonials__modal-timeline-input";
      seek.min = "0";
      seek.max = "1000";
      seek.step = "1";
      seek.value = "0";
      seek.setAttribute("aria-label", "Video timeline");
      timeline.appendChild(seek);
      this.modalMedia.appendChild(timeline);

      let dragging = false;
      const formatTime = (seconds) => {
        const total = Math.max(0, Math.floor(seconds || 0));
        const minutes = Math.floor(total / 60);
        const remain = total % 60;
        return `${minutes}:${String(remain).padStart(2, "0")}`;
      };
      const seekToRatio = (ratio) => {
        const duration = video.duration;
        if (!duration || !Number.isFinite(duration)) return;
        const clamped = Math.min(1, Math.max(0, ratio));
        video.currentTime = clamped * duration;
        seek.style.setProperty("--vt-progress", `${clamped * 100}%`);
      };
      const paintTimeline = () => {
        const duration = video.duration;
        if (!duration || !Number.isFinite(duration)) return;
        const ratio = dragging
          ? Number(seek.value) / 1000
          : Math.min(1, Math.max(0, video.currentTime / duration));
        if (!dragging) seek.value = String(Math.round(ratio * 1000));
        seek.style.setProperty("--vt-progress", `${ratio * 100}%`);
        seek.setAttribute("aria-valuetext", `${formatTime(video.currentTime)} of ${formatTime(duration)}`);
      };
      const tick = () => {
        paintTimeline();
        if (video.isConnected) requestAnimationFrame(tick);
      };

      seek.addEventListener("pointerdown", () => {
        dragging = true;
      });
      seek.addEventListener("pointerup", () => {
        dragging = false;
      });
      seek.addEventListener("pointercancel", () => {
        dragging = false;
      });
      seek.addEventListener("input", () => {
        seekToRatio(Number(seek.value) / 1000);
      });
      seek.addEventListener("change", () => {
        dragging = false;
        paintTimeline();
      });
      seek.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        event.stopPropagation();
        const duration = video.duration;
        if (!duration || !Number.isFinite(duration)) return;
        const delta = event.key === "ArrowRight" ? 5 : -5;
        video.currentTime = Math.min(duration, Math.max(0, video.currentTime + delta));
        paintTimeline();
      });
      video.addEventListener("loadedmetadata", paintTimeline);
      requestAnimationFrame(tick);

      const syncUi = () => {
        const paused = video.paused;
        toggleBtn.classList.toggle("is-paused", paused);
        toggleBtn.setAttribute("aria-label", paused ? "Play" : "Pause");
      };

      const togglePlayback = (event) => {
        if (this._suppressVideoClick) {
          this._suppressVideoClick = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        if (video.paused) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      };

      toggleBtn.addEventListener("click", togglePlayback);
      video.addEventListener("click", togglePlayback);
      video.addEventListener("play", syncUi);
      video.addEventListener("pause", syncUi);
      video.addEventListener("ended", syncUi);
      syncUi();
    }

    bindModalGestures() {
      if (this._gesturesBound || !this.modal) return;
      this._gesturesBound = true;
      this.modal.addEventListener("click", this._onScrubClick);
      const scrubber = this.modal.querySelector("[data-vt-scrubber]");
      const dialog = this.modal.querySelector(".m-video-testimonials__modal-dialog");
      if (scrubber) {
        scrubber.addEventListener("pointerdown", this._onScrubPointerDown);
        scrubber.addEventListener("pointermove", this._onScrubPointerMove);
        scrubber.addEventListener("pointerup", this._onScrubPointerUp);
        scrubber.addEventListener("pointercancel", this._onScrubPointerUp);
      }
      if (dialog) {
        dialog.addEventListener("pointerdown", this._onFlickDown);
        dialog.addEventListener("pointermove", this._onFlickMove);
        dialog.addEventListener("pointerup", this._onFlickUp);
        dialog.addEventListener("pointercancel", this._onFlickUp);
      }
    }

    unbindModalGestures() {
      if (!this._gesturesBound || !this.modal) return;
      this._gesturesBound = false;
      this.modal.removeEventListener("click", this._onScrubClick);
      const scrubber = this.modal.querySelector("[data-vt-scrubber]");
      const dialog = this.modal.querySelector(".m-video-testimonials__modal-dialog");
      if (scrubber) {
        scrubber.removeEventListener("pointerdown", this._onScrubPointerDown);
        scrubber.removeEventListener("pointermove", this._onScrubPointerMove);
        scrubber.removeEventListener("pointerup", this._onScrubPointerUp);
        scrubber.removeEventListener("pointercancel", this._onScrubPointerUp);
      }
      if (dialog) {
        dialog.removeEventListener("pointerdown", this._onFlickDown);
        dialog.removeEventListener("pointermove", this._onFlickMove);
        dialog.removeEventListener("pointerup", this._onFlickUp);
        dialog.removeEventListener("pointercancel", this._onFlickUp);
      }
    }

    ensureScrubber(scrollIntoView) {
      const scrubber = this.modal.querySelector("[data-vt-scrubber]");
      const track = this.modal.querySelector("[data-vt-scrubber-track]");
      if (!scrubber || !track) return;

      const triggers = this.playableTriggers();
      if (triggers.length < 2) {
        scrubber.hidden = true;
        return;
      }

      scrubber.hidden = false;
      const key = triggers.map((trigger) => trigger.dataset.vtIndex || trigger.dataset.videoUrl || trigger.dataset.videoId).join("|");
      if (this._scrubKey !== key) {
        this._scrubKey = key;
        track.replaceChildren();
        triggers.forEach((trigger, index) => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "m-video-testimonials__modal-scrub";
          button.dataset.vtScrub = String(index);
          button.setAttribute("aria-label", trigger.dataset.vtLabel || trigger.getAttribute("aria-label") || "Play video");
          const source = trigger.closest(".m-video-testimonials__card")?.querySelector(".m-video-testimonials__thumb");
          if (source) {
            const img = document.createElement("img");
            img.src = source.currentSrc || source.src;
            img.alt = "";
            img.draggable = false;
            button.appendChild(img);
          }
          track.appendChild(button);
        });
      }
      this.syncScrubber(scrollIntoView);
    }

    syncScrubber(scrollIntoView) {
      const track = this.modal?.querySelector("[data-vt-scrubber-track]");
      if (!track) return;
      const current = this.canonicalTrigger(this.activeTrigger);
      const index = this.playableTriggers().indexOf(current);
      let activeBtn = null;
      track.querySelectorAll("[data-vt-scrub]").forEach((button) => {
        const active = Number(button.dataset.vtScrub) === index;
        button.classList.toggle("is-active", active);
        if (active) {
          button.setAttribute("aria-current", "true");
          activeBtn = button;
        } else {
          button.removeAttribute("aria-current");
        }
      });
      if (!activeBtn || scrollIntoView === false) return;
      const left = activeBtn.offsetLeft - (track.clientWidth - activeBtn.offsetWidth) / 2;
      const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      track.scrollTo({ left: Math.max(0, left), behavior: reduce ? "auto" : "smooth" });
    }

    _onScrubClick(event) {
      const button = event.target.closest("[data-vt-scrub]");
      if (!button || !this.modal?.contains(button)) return;
      if (this._scrubDragged) {
        this._scrubDragged = false;
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.commitScrub(button);
    }

    _onScrubPointerDown(event) {
      if (event.button !== 0) return;
      this._scrubStartX = event.clientX;
      this._scrubLastX = event.clientX;
      this._scrubDragged = false;
      this._scrubbing = true;
    }

    _onScrubPointerMove(event) {
      if (!this._scrubbing || this._scrubStartX == null) return;
      this._scrubLastX = event.clientX;
      if (Math.abs(event.clientX - this._scrubStartX) > 16) {
        this._scrubDragged = true;
        if (event.currentTarget.setPointerCapture) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      }
    }

    _onScrubPointerUp() {
      if (!this._scrubbing) return;
      const dragged = this._scrubDragged;
      const dx = (this._scrubLastX ?? this._scrubStartX) - this._scrubStartX;
      this._scrubbing = false;
      this._scrubStartX = null;
      window.setTimeout(() => {
        this._scrubDragged = false;
      }, 0);
      if (!dragged) return;
      const track = this.modal?.querySelector("[data-vt-scrubber-track]");
      if (track && track.scrollWidth <= track.clientWidth + 2) {
        if (Math.abs(dx) >= 24) this.stepVideo(dx < 0 ? 1 : -1);
        return;
      }
      if (this._scrubSettleTimer) window.clearTimeout(this._scrubSettleTimer);
      this._scrubSettleTimer = window.setTimeout(() => {
        this._scrubSettleTimer = null;
        this.selectCenteredScrub();
      }, 160);
    }

    /* A flick of the strip lands on whichever thumbnail sits in the middle. */
    selectCenteredScrub() {
      const track = this.modal?.querySelector("[data-vt-scrubber-track]");
      if (!track || track.scrollWidth <= track.clientWidth + 2) return;
      const center = track.getBoundingClientRect().left + track.clientWidth / 2;
      let closest = null;
      let best = Infinity;
      track.querySelectorAll("[data-vt-scrub]").forEach((button) => {
        const rect = button.getBoundingClientRect();
        const distance = Math.abs(rect.left + rect.width / 2 - center);
        if (distance < best) {
          best = distance;
          closest = button;
        }
      });
      if (closest) this.commitScrub(closest);
    }

    commitScrub(button) {
      const next = this.playableTriggers()[Number(button.dataset.vtScrub)];
      if (!next || this.canonicalTrigger(this.activeTrigger) === next) return;
      this.openModal(next, { focusClose: false });
    }

    _onFlickDown(event) {
      if (event.button !== 0) return;
      if (event.target.closest("[data-vt-modal-close], .m-video-testimonials__modal-toggle, .m-video-testimonials__modal-timeline")) return;
      this._flick = { id: event.pointerId, x: event.clientX, y: event.clientY, active: true };
    }

    _onFlickMove(event) {
      const flick = this._flick;
      if (!flick?.active || event.pointerId !== flick.id) return;
      const dx = event.clientX - flick.x;
      const dy = event.clientY - flick.y;
      if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
        flick.dragging = true;
        if (event.currentTarget.setPointerCapture) {
          event.currentTarget.setPointerCapture(event.pointerId);
        }
      }
    }

    _onFlickUp(event) {
      const flick = this._flick;
      if (!flick?.active || event.pointerId !== flick.id) return;
      const dx = event.clientX - flick.x;
      const dy = event.clientY - flick.y;
      this._flick = null;
      if (!flick.dragging || Math.abs(dx) < 48 || Math.abs(dx) < Math.abs(dy)) return;
      this._suppressVideoClick = true;
      const token = (this._suppressToken = (this._suppressToken || 0) + 1);
      window.setTimeout(() => {
        if (this._suppressToken === token) this._suppressVideoClick = false;
      }, 350);
      this.stepVideo(dx < 0 ? 1 : -1);
    }

    buildMedia(type, videoUrl, videoId) {
      if (type === "shopify" && videoUrl) {
        const video = document.createElement("video");
        video.src = videoUrl;
        video.controls = false;
        video.playsInline = true;
        video.autoplay = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.setAttribute("disablepictureinpicture", "");
        video.setAttribute("controlslist", "nodownload nofullscreen noremoteplayback");
        return video;
      }

      if (type === "youtube" && videoId) {
        const iframe = document.createElement("iframe");
        iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?enablejsapi=1&autoplay=1&rel=0&controls=1&fs=0&modestbranding=1`;
        iframe.className = "js-youtube";
        iframe.allow = "autoplay; encrypted-media";
        iframe.title = "Video testimonial";
        return iframe;
      }

      if (type === "vimeo" && videoId) {
        const iframe = document.createElement("iframe");
        iframe.src = `https://player.vimeo.com/video/${encodeURIComponent(videoId)}?autoplay=1&controls=1`;
        iframe.className = "js-vimeo";
        iframe.allow = "autoplay; encrypted-media";
        iframe.title = "Video testimonial";
        return iframe;
      }

      return null;
    }
  }

  customElements.define("m-video-testimonials", MVideoTestimonials);
}
